import { Suspense, lazy, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Routes, Route, useParams } from 'react-router-dom';
import { api } from './api.js';
import { t, zetThema, zetTaal, huidigeTaal } from './i18n.js';
import Schil from './components/Schil.jsx';
import Aanmelden from './pages/Aanmelden.jsx';
import Overzicht from './pages/Overzicht.jsx';
import Logboek from './pages/Logboek.jsx';
import Bronnen from './pages/Bronnen.jsx';
import Alerts from './pages/Alerts.jsx';
import Alert from './pages/Alert.jsx';
/* De kaart neemt de wereldkaart en d3 mee; die laden pas als iemand de kaart opent. */
const Kaart = lazy(() => import('./pages/Kaart.jsx'));
/* Het SOC-scherm: een eigen weergave, zonder schil en zonder aanmelding (een wandscherm heeft een sleutel). */
const Soc = lazy(() => import('./pages/Soc.jsx'));
import Bron from './pages/Bron.jsx';
import Vpn from './pages/Vpn.jsx';
import Identity from './pages/Identity.jsx';
import Beveiligingsstatus from './pages/Beveiligingsstatus.jsx';
import VolgendeStappen from './pages/VolgendeStappen.jsx';
import Duo from './pages/Duo.jsx';
import Firewall from './pages/Firewall.jsx';
import Mail from './pages/Mail.jsx';
import Blokkades from './pages/Blokkades.jsx';
import Blokkade from './pages/Blokkade.jsx';
import Scans from './pages/Scans.jsx';
import ScanAdres from './pages/ScanAdres.jsx';
import BronNieuw from './pages/BronNieuw.jsx';
import Beheer from './pages/Beheer.jsx';
import Instellingen from './pages/Instellingen.jsx';
import Wachtwoord from './pages/Wachtwoord.jsx';
import Klant from './pages/Klant.jsx';
import Uitleg, { Nieuw } from './pages/Uitleg.jsx';
import Umbrella from './pages/Umbrella.jsx';
import Endpoints from './pages/Endpoints.jsx';
import Modules from './pages/Modules.jsx';
import Groep from './pages/Groep.jsx';
import { GeenToegang } from './pages/Meldingen.jsx';

export default function App() {
  if (window.location.pathname === '/soc') {
    /* De taal van een wandscherm staat in de link; vóór het eerste beeld, anders flitst de andere taal. */
    const taal = new URLSearchParams(window.location.search).get('lang');
    if (taal) zetTaal(taal);
    return <Suspense fallback={null}><Soc /></Suspense>;
  }
  return <Werkplek />;
}

function Werkplek() {
  /**
   * Wie ben ik, en wat mag ik. Wat iemand mág bepaalt de server bij elk verzoek opnieuw; hier wordt
   * het alleen gebruikt om te tonen wat er te doen valt.
   */
  const ik = useQuery({ queryKey: ['ik'], queryFn: api.wieBenIk, retry: false });

  const thema = ik.data?.data?.settings?.theme;
  const taal = ik.data?.data?.settings?.language;
  useEffect(() => {
    if (thema) zetThema(thema);
    /* Een andere taal laadt de pagina één keer opnieuw; daarna staat hij ook in deze browser. */
    if (taal && taal !== huidigeTaal()) { zetTaal(taal); window.location.reload(); }
  }, [thema, taal]);

  if (ik.isPending) return <p className="muted" style={{ padding: 40 }}>{t('Laden…')}</p>;
  if (ik.isError) return <Aanmelden onGelukt={() => ik.refetch()} />;

  const mij = ik.data.data;
  /* Een klant via het klantportaal (alleen op de publieke ingang): geen schil, geen schermen voor medewerkers. */
  if (mij.soort === 'klant') return <Klant mij={mij} onAfmelden={() => api.afmelden().then(() => ik.refetch())} />;
  const mag = (recht) => mij.permissions.includes(recht);
  if (mij.mustChangePassword) return <Wachtwoord verplicht onKlaar={() => ik.refetch()} />;

  return (
    <Routes>
      {/* Meekijken als klant: het scherm van de klant, zonder de schil van medewerkers. */}
      <Route path="/meekijken/:code/*" element={mag('alerts.lezen') ? <Meekijken /> : <GeenToegang />} />
      <Route path="*" element={<Medewerker mij={mij} mag={mag} ik={ik} />} />
    </Routes>
  );
}

/** Meekijken met een klant (docs/besluiten/meekijken-met-een-klant.md): wat de klant ziet, alleen lezen. */
function Meekijken() {
  const { code } = useParams();
  const q = useQuery({ queryKey: ['meekijken', code], queryFn: () => api.meekijken(code), staleTime: Infinity });
  if (q.isPending) return <p className="muted" style={{ padding: 40 }}>{t('Laden…')}</p>;
  if (q.isError) return <p style={{ padding: 40 }}>{t(q.error.message)}</p>;
  const { tenant, modules } = q.data.data;
  const alsKlant = { tenants: [tenant], email: null, roleLabels: [], permissions: ['alerts.lezen'] };
  return <Klant mij={alsKlant} meekijken={{ code: tenant.code, name: tenant.name, modules }} />;
}

function Medewerker({ mij, mag, ik }) {
  return (
    <Schil mij={mij} onAfmelden={() => api.afmelden().then(() => ik.refetch())} opnieuw={() => ik.refetch()}>
      {(klant) => (
        <Routes>
          <Route path="/" element={<Overzicht mij={mij} klant={klant} />} />
          <Route path="/groep/:id" element={<Groep gekozen={klant} mag={mag} />} />
          <Route path="/alerts" element={mag('alerts.lezen') ? <Alerts klant={klant} /> : <GeenToegang />} />
          <Route path="/alerts/:nummer" element={mag('alerts.lezen') ? <Alert mij={mij} /> : <GeenToegang />} />
          <Route path="/kaart" element={mag('alerts.lezen')
            ? <Suspense fallback={<p className="muted">{t('Laden…')}</p>}><Kaart klant={klant} mij={mij} /></Suspense> : <GeenToegang />} />
          <Route path="/scans" element={mag('alerts.lezen') ? <Scans klant={klant} mij={mij} /> : <GeenToegang />} />
          <Route path="/scans/:id" element={mag('alerts.lezen') ? <ScanAdres /> : <GeenToegang />} />
          <Route path="/umbrella" element={mag('alerts.lezen') ? <Umbrella haal={(d) => api.umbrellaGrafieken(klant, d)} sleutel={['umbrella', klant]}
            sub={klant === 'alle' ? t('Alle klanten') : mij.tenants.find((k) => String(k.id) === String(klant))?.name} /> : <GeenToegang />} />
          <Route path="/modules" element={mag('alerts.lezen') ? <Modules klant={klant} mij={mij} /> : <GeenToegang />} />
          <Route path="/endpoints" element={mag('alerts.lezen') ? <Endpoints haal={(d) => api.endpoints(klant, d)} sleutel={['endpoints', klant]}
            sub={klant === 'alle' ? t('Alle klanten') : mij.tenants.find((k) => String(k.id) === String(klant))?.name} /> : <GeenToegang />} />
          <Route path="/blokkades" element={mag('alerts.lezen') ? <Blokkades mij={mij} klant={klant} /> : <GeenToegang />} />
          <Route path="/blokkades/:nummer" element={mag('alerts.lezen') ? <Blokkade mij={mij} /> : <GeenToegang />} />
          <Route path="/mail" element={mag('alerts.lezen') ? <Mail klant={klant} /> : <GeenToegang />} />
          <Route path="/firewall" element={mag('alerts.lezen') ? <Firewall klant={klant} /> : <GeenToegang />} />
          <Route path="/vpn" element={mag('alerts.lezen') ? <Vpn klant={klant} /> : <GeenToegang />} />
          <Route path="/identity" element={mag('alerts.lezen') ? <Identity klant={klant} /> : <GeenToegang />} />
          <Route path="/duo" element={mag('alerts.lezen') ? <Duo klant={klant} /> : <GeenToegang />} />
          <Route path="/status" element={mag('alerts.lezen') ? <Beveiligingsstatus key={klant} klant={klant} /> : <GeenToegang />} />
          <Route path="/volgende-stappen" element={mag('alerts.lezen') ? <VolgendeStappen key={klant} klant={klant} /> : <GeenToegang />} />
          <Route path="/bronnen" element={mag('alerts.lezen') ? <Bronnen mij={mij} klant={klant} /> : <GeenToegang />} />
          <Route path="/bronnen/nieuw" element={mag('bronnen.beheren') ? <BronNieuw mij={mij} klant={klant} /> : <GeenToegang />} />
          <Route path="/bronnen/:id" element={mag('alerts.lezen') ? <Bron mij={mij} /> : <GeenToegang />} />
          <Route path="/logboek" element={mag('logboek.lezen') ? <Logboek klant={klant} /> : <GeenToegang />} />
          <Route path="/beheer" element={mag('rechten.beheren') ? <Beheer mij={mij} /> : <GeenToegang />} />
          <Route path="/instellingen" element={<Instellingen mij={mij} opnieuw={() => ik.refetch()} />} />
          <Route path="/uitleg" element={<Uitleg versie={mij.version} />} />
          <Route path="/nieuw" element={<Nieuw versie={mij.version} />} />
          <Route path="/wachtwoord" element={<Wachtwoord onKlaar={() => ik.refetch()} />} />
          <Route path="*" element={<p className="muted">{t('Niet gevonden')}</p>} />
        </Routes>
      )}
    </Schil>
  );
}
